import React, { useState, useEffect, useRef, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../context/AuthContext';
import API_BASE_URL from '../config';
import './ClientItinerary.css';
import airportsData from '../airports.json';
import jsPDF from 'jspdf';


function ClientItinerary({ tripData = null, clientNameData = null }) {
  const [trip, setTrip] = useState(null);
  const [clientName, setClientName] = useState('');
  const [loading, setLoading] = useState(true);
  const [menuOpen, setMenuOpen] = useState(false);
  const [expandedBookings, setExpandedBookings] = useState({});
  const menuRef = useRef(null);
  const { user, logout } = useContext(AuthContext);
  const navigate = useNavigate();

  useEffect(() => {
    // If trip data is provided as props, use it directly
    if (tripData && clientNameData) {
      setTrip(tripData);
      setClientName(clientNameData);
      setLoading(false);
      return;
    }

    // Only check authentication if no props are provided (normal client usage)
    if (!user || user.role !== 'client') {
      navigate('/login');
      return;
    }

    const fetchData = async () => {
      try {
        const cachedTrip = localStorage.getItem('clientItineraryTrip');
        const cachedClientName = localStorage.getItem('clientItineraryName');

        if (navigator.onLine) {
          const [tripResponse, clientResponse] = await Promise.all([
            axios.get(`${API_BASE_URL}/api/trips?clientId=${user.clientId}`),
            axios.get(`${API_BASE_URL}/api/clients?userId=${user.userId}`)
          ]);

          const tripData = tripResponse.data.data;
          setTrip(tripData);
          localStorage.setItem('clientItineraryTrip', JSON.stringify(tripData));

          const clientData = clientResponse.data.data[0];
          const name = clientData?.name || 'Client';
          setClientName(name);
          localStorage.setItem('clientItineraryName', name);
        } else if (cachedTrip && cachedClientName) {
          setTrip(JSON.parse(cachedTrip));
          setClientName(cachedClientName);
        } else {
          setTrip(null);
          setClientName('Client');
        }
      } catch (error) {
        console.error('Error fetching itinerary:', error);
        const cachedTrip = localStorage.getItem('clientItineraryTrip');
        const cachedClientName = localStorage.getItem('clientItineraryName');
        if (cachedTrip && cachedClientName) {
          setTrip(JSON.parse(cachedTrip));
          setClientName(cachedClientName);
        } else {
          setTrip(null);
          setClientName('Client');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [user, navigate]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    document.title = trip?.destination ? `${clientName}'s Trip to ${trip.destination}` : 'Client Itinerary';
  }, [trip, clientName]);

  const handleLogout = () => {
    logout();
    localStorage.removeItem('clientItineraryTrip');
    localStorage.removeItem('clientItineraryName');
    navigate('/login');
  };

  const toggleMenu = () => setMenuOpen(!menuOpen);

  const toggleDetails = (bookingId, isStart) => {
    const key = `${bookingId}-${isStart ? 'start' : 'end'}`;
    setExpandedBookings(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const downloadPDF = () => {
    const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  
    // Header
    doc.setFontSize(18);
    doc.text(`${clientName}'s Trip to ${trip?.destination || 'Unknown'}`, 10, 10);
    doc.setFontSize(12);
    doc.text(`Client: ${clientName}`, 10, 20);
    doc.text(`Dates: ${trip?.startDate ? new Date(trip.startDate).toLocaleDateString() : 'N/A'} - ${trip?.endDate ? new Date(trip.endDate).toLocaleDateString() : 'N/A'}`, 10, 30);
    doc.text(`Duration: ${trip?.durationInDays || 'N/A'} days`, 10, 40);
  
    // Replicate app's day-by-day itinerary
    if (trip) {
      const startDate = new Date(trip.startDate);
      const durationInDays = Math.ceil((new Date(trip.endDate) - startDate) / (1000 * 60 * 60 * 24)) + 1;
      const bookingsByDay = new Map();
  
      // Group bookings by day
      trip.bookings.forEach(booking => {
        const bookingStart = new Date(booking.startDate);
        const bookingEnd = new Date(booking.endDate);
        const startDateStr = `${bookingStart.getUTCFullYear()}-${String(bookingStart.getUTCMonth() + 1).padStart(2, '0')}-${String(bookingStart.getUTCDate()).padStart(2, '0')}`;
        const endDateStr = `${bookingEnd.getUTCFullYear()}-${String(bookingEnd.getUTCMonth() + 1).padStart(2, '0')}-${String(bookingEnd.getUTCDate()).padStart(2, '0')}`;
  
        if (!bookingsByDay.has(startDateStr)) bookingsByDay.set(startDateStr, []);
        bookingsByDay.get(startDateStr).push({ booking, isStart: true });
  
        if (startDateStr !== endDateStr) {
          if (!bookingsByDay.has(endDateStr)) bookingsByDay.set(endDateStr, []);
          bookingsByDay.get(endDateStr).push({ booking, isStart: false });
        }
      });
  
      let yOffset = 50;
      const days = [];
      let rangeStart = null;
  
      // Generate day-by-day structure
      for (let i = 0; i < durationInDays; i++) {
        const dayDate = new Date(startDate);
        dayDate.setUTCDate(startDate.getUTCDate() + i);
        const dayDateStr = `${dayDate.getUTCFullYear()}-${String(dayDate.getUTCMonth() + 1).padStart(2, '0')}-${String(dayDate.getUTCDate()).padStart(2, '0')}`;
  
        if (bookingsByDay.has(dayDateStr)) {
          if (rangeStart !== null) {
            days.push({ start: rangeStart, end: i - 1, bookings: [] });
            rangeStart = null;
          }
          const dayBookings = bookingsByDay.get(dayDateStr).sort((a, b) => {
            const timeA = a.isStart ? new Date(a.booking.startDate).getTime() : new Date(a.booking.endDate).getTime();
            const timeB = b.isStart ? new Date(b.booking.startDate).getTime() : new Date(b.booking.endDate).getTime();
            return timeA - timeB;
          });
          days.push({ start: i, end: i, bookings: dayBookings });
        } else {
          if (rangeStart === null) rangeStart = i;
          if (i === durationInDays - 1 && rangeStart !== null) {
            days.push({ start: rangeStart, end: i, bookings: [] });
          }
        }
      }
  
      // Render days and bookings
      days.forEach(dayRange => {
        const startDay = new Date(startDate);
        startDay.setUTCDate(startDate.getUTCDate() + dayRange.start);
        const endDay = new Date(startDate);
        endDay.setUTCDate(startDate.getUTCDate() + dayRange.end);
  
        // Day Header
        doc.setFontSize(14);
        doc.text(`Day ${dayRange.start + 1}: ${new Intl.DateTimeFormat('en-US', { weekday: 'long', month: 'short', day: 'numeric', year: 'numeric' }).format(startDay)}`, 10, yOffset);
        yOffset += 5;
        if (dayRange.start !== dayRange.end) {
          doc.text(`- Day ${dayRange.end + 1}: ${new Intl.DateTimeFormat('en-US', { weekday: 'long', month: 'short', day: 'numeric', year: 'numeric' }).format(endDay)}`, 10, yOffset);
          yOffset += 5;
        }
  
        // Bookings
        if (dayRange.bookings.length > 0) {
          dayRange.bookings.forEach(({ booking, isStart }) => {
            const mainInfo = renderBookingMainInfo(booking, isStart);
            if (!mainInfo) return;
  
            const eventType = booking.type === 'Event' ? (booking.details?.eventType === 'Other' ? 'Event' : booking.details?.eventType || 'Event') : '';
            doc.setFontSize(10);
            doc.text(`${booking.type === 'Event' ? `${booking.type} (${eventType})` : booking.type}: ${booking.bookingName}`, 15, yOffset);
            yOffset += 5;
  
            // Extract text from mainInfo
            let mainInfoText = '';
            if (typeof mainInfo === 'string') {
              mainInfoText = mainInfo;
            } else if (mainInfo.props && mainInfo.props.children) {
              const extractText = (children) => {
                if (typeof children === 'string') return children;
                if (Array.isArray(children)) return children.map(extractText).join('');
                if (children.props && children.props.children) return extractText(children.props.children);
                return '';
              };
              mainInfoText = extractText(mainInfo.props.children);
            }
            doc.text(`  ${mainInfoText}`, 15, yOffset);
            yOffset += 5;
  
            // Booking Details
            const details = [];
            switch (booking.type) {
              case 'Flights':
                if (isStart) {
                  if (booking.details?.confirmationNumber) details.push(`Confirmation #: ${booking.details.confirmationNumber}`);
                  if (booking.details?.departureAirportIata) details.push(`Departure Airport: ${getAirportFromIata(booking.details.departureAirportIata)}`);
                  if (booking.details?.arrivalAirportIata) details.push(`Arrival Airport: ${getAirportFromIata(booking.details.arrivalAirportIata)}`);
                  if (booking.details?.luggage) details.push(`Luggage: ${booking.details.luggage.replace(/\n/g, '; ')}`);
                  if (booking.details?.notes) details.push(`Notes: ${booking.details.notes}`);
                }
                break;
              case 'Hotels':
              case 'Homes/Villas':
              case 'Restaurant':
                if (isStart) {
                  if (booking.details?.confirmationNumber) details.push(`Confirmation #: ${booking.details.confirmationNumber}`);
                  if (booking.details?.nameOnReservation) details.push(`Name on Reservation: ${booking.details.nameOnReservation}`);
                  if (booking.type === 'Hotels' && booking.details?.roomType) details.push(`Room Type: ${booking.details.roomType}`);
                  if (booking.type === 'Restaurant' && booking.details?.partySize) details.push(`Party Size: ${booking.details.partySize}`);
                  if (booking.details?.location) details.push(`Location: ${booking.details.location}`);
                  if (booking.details?.notes) details.push(`Notes: ${booking.details.notes}`);
                }
                break;
              case 'Car Rental':
                if (isStart) {
                  if (booking.details?.confirmationNumber) details.push(`Confirmation #: ${booking.details.confirmationNumber}`);
                  if (booking.details?.nameOnReservation) details.push(`Name on Reservation: ${booking.details.nameOnReservation}`);
                  if (booking.details?.vehicleType) details.push(`Vehicle Type: ${booking.details.vehicleType}`);
                  if (booking.details?.pickupLocation) details.push(`Pickup Location: ${booking.details.pickupLocation}`);
                  if (booking.details?.notes) details.push(`Notes: ${booking.details.notes}`);
                } else {
                  if (booking.details?.dropoffLocation) details.push(`Drop-off Location: ${booking.details.dropoffLocation}`);
                }
                break;
              case 'Cruise':
                if (isStart) {
                  if (booking.details?.confirmationNumber) details.push(`Confirmation #: ${booking.details.confirmationNumber}`);
                  if (booking.details?.departurePort) details.push(`Departure Port: ${booking.details.departurePort}`);
                  if (booking.details?.notes) details.push(`Notes: ${booking.details.notes}`);
                } else {
                  if (booking.details?.arrivalPort) details.push(`Arrival Port: ${booking.details.arrivalPort}`);
                  if (booking.details?.notes) details.push(`Notes: ${booking.details.notes}`);
                }
                break;
              case 'DMCs':
              case 'Packages':
              case 'Transportation':
              case 'Multiday Tours':
              case 'Other':
              case 'Day Tours & Activities':
              case 'Insurance':
                if (isStart) {
                  if (booking.details?.confirmationNumber) details.push(`Confirmation #: ${booking.details.confirmationNumber}`);
                  if (booking.details?.nameOnReservation) details.push(`Name on Reservation: ${booking.details.nameOnReservation}`);
                  if (booking.details?.notes) details.push(`Notes: ${booking.details.notes}`);
                }
                break;
              case 'Event':
                if (isStart) {
                  if (booking.details?.confirmationNumber) details.push(`Confirmation #: ${booking.details.confirmationNumber}`);
                  if (booking.details?.nameOnReservation) details.push(`Name on Reservation: ${booking.details.nameOnReservation}`);
                  if (booking.details?.venueName) details.push(`Venue: ${booking.details.venueName}`);
                  if (booking.details?.location) details.push(`Location: ${booking.details.location}`);
                  if (booking.details?.notes) details.push(`Notes: ${booking.details.notes}`);
                }
                break;
            }
  
            if (details.length > 0) {
              details.forEach(detail => {
                doc.text(`  ${detail}`, 15, yOffset);
                yOffset += 5;
              });
            }
            yOffset += 2;
          });
        } else {
          doc.setFontSize(10);
          doc.text('No Events Scheduled', 15, yOffset);
          yOffset += 5;
        }
  
        yOffset += 5;
        if (yOffset > 260) {
          doc.addPage();
          yOffset = 10;
        }
      });
    }
  
    doc.save(`${clientName}_Itinerary_${trip?.destination || 'Trip'}.pdf`);
  };

  const getIconClass = (type, details = {}, isStart = true) => {
    if (type === 'Transportation' && details.transportType) {
      const transportIcons = {
        'Taxi': 'icon-taxi',
        'Limousine': 'icon-limousine',
        'Shuttle': 'icon-shuttle',
        'Bus': 'icon-bus',
        'Van': 'icon-van'
      };
      return transportIcons[details.transportType] || 'icon-transport-default';
    }

    if (type === 'Event' && details.eventType) {
      const eventIcons = {
        'Concert': 'icon-event-concert',
        'Festival': 'icon-event-festival',
        'Wedding': 'icon-wedding',
        'Conference': 'icon-event-conference',
        'Other': 'icon-event-other'
      };
      return eventIcons[details.eventType] || 'icon-event-default';
    }

    if (type === 'Day Tours & Activities') {
    const activityIcons = {
      'Hike': 'icon-hike',
      'Adventure': 'icon-adventure',
      'Cultural': 'icon-cultural',
      'Cooking Class': 'icon-cooking',
      'Wildlife': 'icon-wildlife',
      'Other': 'icon-tour-default'
    };
    return activityIcons[details.activityType] || 'icon-tour';
  }

    if (type === 'Car Rental' && !isStart) {
      return 'icon-carrental-dropoff';
    }

    if (type === 'Hotels' && !isStart) {
      return 'icon-hotel-out';
    }
  
    const iconClasses = {
      'Hotels': 'icon-hotel-in',
      'Flights': 'icon-flight',
      'Cruise': 'icon-cruise',
      'DMCs': 'icon-dmc',
      'Homes/Villas': 'icon-home',
      'Insurance': 'icon-insurance',
      'Packages': 'icon-package',
      'Transportation': 'icon-transport',
      'Multiday Tours': 'icon-tour-multi',
      'Other': 'icon-other',
      'Restaurant': 'icon-restaurant',
      'Car Rental': 'icon-carrental',
      'Transport': 'icon-transport-default',
      'AirBnb': 'icon-airbnb'
    };
    return iconClasses[type] || 'icon-default';
  };

  const formatTime = (date) => {
    const d = new Date(date);
    const hours = String(d.getUTCHours()).padStart(2, '0');
    const minutes = String(d.getUTCMinutes()).padStart(2, '0');
    const period = hours >= 12 ? 'pm' : 'am';
    const displayHours = hours % 12 || 12;
    return `${displayHours}:${minutes} ${period}`;
  };

  const getRestaurantMealType = (date) => {
    const hours = new Date(date).getUTCHours();
    if (hours >= 5 && hours < 11) return 'Breakfast';
    if (hours >= 11 && hours < 14) return 'Brunch';
    if (hours >= 14 && hours < 18) return 'Lunch';
    if (hours >= 18 && hours < 24) return 'Dinner';
    return 'Reservation';
  };

  const getCityFromIata = (airportIata) => {
    const airport = airportsData.find(a => a.IATA === airportIata);
    return airport?.City || 'Unknown';
  };

  const getAirportFromIata = (airportIata) => {
    const airport = airportsData.find(a => a.IATA === airportIata);
    return airport?.Name || 'Unknown';
  };

  const getMapLinkFromIata = (airportIata) => {
    const airport = airportsData.find(a => a.IATA === airportIata);
    return airport?.Latitude && airport.Longitude
      ? `https://maps.google.com/?q=${airport.Latitude},${airport.Longitude}`
      : 'Unknown';
  };

  const renderBookingMainInfo = (booking, isStart) => {
    const startDate = new Date(booking.startDate);
    const endDate = new Date(booking.endDate);
    const startTime = formatTime(startDate);
    const endTime = formatTime(endDate);
    const sameDay = startDate.toISOString().split('T')[0] === endDate.toISOString().split('T')[0];
    const isAllDay = startDate.getUTCHours() === 0 && startDate.getUTCMinutes() === 0 && 
                     endDate.getUTCHours() === 23 && endDate.getUTCMinutes() === 59;

    switch (booking.type) {
      case 'Flights':
        const destinationCity = getCityFromIata(booking.details?.arrivalAirportIata);
        const originCity = getCityFromIata(booking.details?.departureAirportIata);
        if (isStart) {
          return (
            <>
              Flight <strong>{booking.details?.flightNumber || 'Unknown'}</strong> to {destinationCity}<br />
              {startTime} - departs {originCity} ({booking.details?.departureAirportIata})
              {sameDay && <><br />{endTime} - arrives {destinationCity} ({booking.details?.arrivalAirportIata})</>}
            </>
          );
        } else if (!sameDay) {
          return (
            <>
              Flight <strong>{booking.details?.flightNumber || 'Unknown'}</strong> from {originCity}<br />
              {endTime} - arrives {destinationCity} ({booking.details?.arrivalAirportIata})
            </>
          );
        }
        return null;
      case 'Hotels':
        return isStart ? <>{startTime} Check-in at {booking.bookingName}</> : <>{endTime} Check-out from {booking.bookingName}</>;
      case 'AirBnb':
        return isStart ? <>{startTime} Check-in at {booking.bookingName}</> : <>{endTime} Check-out from {booking.bookingName}</>;
      case 'Cruise':
        return isStart ? <>{startTime} Board {booking.bookingName} {booking.details.shipName}</> : <>{endTime} Disembark {booking.bookingName} {booking.details.shipName}</>;
      case 'DMCs':
        return isStart ? <>{startTime} Start service with {booking.bookingName}</> : <>{endTime} End service with {booking.bookingName}</>;
      case 'Homes/Villas':
        return isStart ? <>{startTime} Check-in at {booking.bookingName}</> : <>{endTime} Check-out from {booking.bookingName}</>;
      case 'Insurance':
        return isStart ? <>{startTime} Coverage starts with {booking.bookingName}</> : '';
      case 'Packages':
        return isStart ? <>{startTime} Begin {booking.bookingName}</> : <>{endTime} End {booking.bookingName}</>;
      case 'Transportation':
        return isStart ? <>{startTime} Pickup with {booking.bookingName}</> : <>{endTime} Drop-off with {booking.bookingName}</>;
      case 'Multiday Tours':
        return isStart ? <>{startTime} Start tour with {booking.bookingName}</> : <>{endTime} End tour with {booking.bookingName}</>;
        case 'Day Tours & Activities':
        return isStart ? (isAllDay ? <>All day {booking.bookingName}</> : <>{startTime} - {endTime} {booking.details.activityType || 'Tour'} at {booking.bookingName}</>) : '';
      case 'Other':
        return isStart ? <>{startTime} Start {booking.bookingName}</> : <>{endTime} End {booking.bookingName}</>;
      case 'Restaurant':
        return isStart ? <>{startTime} {getRestaurantMealType(startDate)} at {booking.bookingName}</> : '';
      case 'Car Rental':
        return isStart ? (
          <>{startTime} Pick-up car at {booking.bookingName} {booking.details?.pickupLocation || ''}</>
        ) : (
          <>{endTime} Drop-off car at {booking.bookingName} {booking.details?.dropoffLocation || ''}</>
        );
      case 'Event':
        if (isStart) {
          const eventType = booking.details?.eventType === 'Other' ? 'Event' : booking.details?.eventType || 'Event';
          let description;
          description = `${startTime} Attend ${booking.bookingName} ${eventType}`;
          return isAllDay ? <>All day {booking.bookingName} {eventType}</> : <>{description}</>;
        }
        return '';
      default:
        return isStart ? <>{startTime} Start at {booking.bookingName}</> : <>{endTime} End at {booking.bookingName}</>;
    }
  };

  const renderBookingDetails = (booking, isStart) => {
    const details = [];

    switch (booking.type) {
      case 'Flights':
        if (isStart) {
          const departureAirport = getAirportFromIata(booking.details?.departureAirportIata);
          const arrivalAirport = getAirportFromIata(booking.details?.arrivalAirportIata);
          const departureAirportLink = getMapLinkFromIata(booking.details?.departureAirportIata);
          const arrivalAirportLink = getMapLinkFromIata(booking.details?.arrivalAirportIata);
          if (booking.details?.confirmationNumber) details.push(<p key="conf"><strong>Confirmation #:</strong> {booking.details.confirmationNumber}</p>);
          if (departureAirport) details.push(<p key="dep"><strong>Departure Airport:</strong> <a href={departureAirportLink} target="_blank" rel="noopener noreferrer" className='details-link'>{departureAirport}</a></p>);
          if (arrivalAirport) details.push(<p key="arr"><strong>Arrival Airport:</strong> <a href={arrivalAirportLink} target="_blank" rel="noopener noreferrer" className='details-link'>{arrivalAirport}</a></p>);
          if (booking.details?.luggage) {
            details.push(<p key="luggage-title"><strong>Luggage:</strong></p>);
            details.push(<p key="luggage-break" style={{ margin: '0' }}></p>);
            booking.details.luggage.split('\n').forEach((line, index) => {
              if (line.trim()) details.push(<p key={`luggage-${index}`} style={{ marginLeft: '20px' }}>{line}</p>);
            });
          }
          if (booking.details?.notes) details.push(<p key="notes"><strong>Notes:</strong> {booking.details.notes}</p>);
        }
        break;
      case 'Hotels':
      case 'AirBnb':
      case 'Homes/Villas':
      case 'Restaurant':
      case 'Event':
        if (isStart) {
          if (booking.details?.confirmationNumber) details.push(<p key="conf"><strong>Confirmation #:</strong> {booking.details.confirmationNumber}</p>);
          if (booking.details?.nameOnReservation) details.push(<p key="name"><strong>Name on Reservation:</strong> {booking.details.nameOnReservation}</p>);
          if (booking.type === 'Hotels' && booking.details?.roomType) details.push(<p key="room"><strong>Room Type:</strong> {booking.details.roomType}</p>);
          if (booking.type === 'Restaurant' && booking.details?.partySize) details.push(<p key="party"><strong>Party Size:</strong> {booking.details.partySize}</p>);
          if (booking.details?.location) details.push(<p key="loc"><strong>Location:</strong> <a href={booking.details.location} target="_blank" rel="noopener noreferrer" className='details-link'>open map</a></p>);
          if (booking.details?.notes) details.push(<p key="notes" style={{ whiteSpace: 'pre-wrap' }}><strong>Notes:</strong> {booking.details.notes}</p>);
        }
        break;
      case 'Car Rental':
        if (isStart) {
          if (booking.details?.confirmationNumber) details.push(<p key="conf"><strong>Confirmation #:</strong> {booking.details.confirmationNumber}</p>);
          if (booking.details?.nameOnReservation) details.push(<p key="name"><strong>Name on Reservation:</strong> {booking.details.nameOnReservation}</p>);
          if (booking.details?.vehicleType) details.push(<p key="vehicle"><strong>Vehicle Type:</strong> {booking.details.vehicleType}</p>);
          if (booking.details?.pickupLocation) details.push(<p key="pickupLoc"><strong>Pickup Location:</strong>{' '}
            {booking.details.pickupMapLink ? (<a href={booking.details.pickupMapLink} target="_blank" rel="noopener noreferrer" className="details-link">{booking.details.pickupLocation}</a>) : booking.details.pickupLocation}
          </p>);
          if (booking.details?.notes) details.push(<p key="notes"style={{ whiteSpace: 'pre-wrap' }}><strong>Notes:</strong> {booking.details.notes}</p>);
        } else {
          if (booking.details?.dropoffLocation) details.push(<p key="dropoffLoc"><strong>Drop-off Location:</strong>{' '}
            {booking.details.dropoffMapLink ? (<a href={booking.details.dropoffMapLink} target="_blank" rel="noopener noreferrer" className="details-link">{booking.details.dropoffLocation}</a>) : booking.details.dropoffLocation}
          </p>);
        }
        break;
      case 'Cruise':
        if (isStart) {
          if (booking.details?.confirmationNumber) details.push(<p key="conf"><strong>Confirmation #:</strong> {booking.details.confirmationNumber}</p>);
          if (booking.details?.departurePort) details.push(<p key="depPort"><strong>Departure Port:</strong> <a href={booking.details.departurePortLink} target="_blank" rel="noopener noreferrer" className='details-link'>{booking.details.departurePort}</a></p>);
          if (booking.details?.notes) details.push(<p key="notes"><strong>Notes:</strong> {booking.details.notes}</p>);
        } else {
          if (booking.details?.arrivalPort) details.push(<p key="arrPort"><strong>Arrival Port:</strong> <a href={booking.details.arrivalPortLink} target="_blank" rel="noopener noreferrer" className='details-link'>{booking.details.arrivalPort}</a></p>);
          if (booking.details?.notes) details.push(<p key="notes" style={{ whiteSpace: 'pre-wrap' }}><strong>Notes:</strong> {booking.details.notes}</p>);
        }
        break;
      case 'DMCs':
      case 'Packages':
      case 'Transportation':
      case 'Multiday Tours':
      case 'Other':
      case 'Day Tours & Activities':
      case 'Insurance':
        if (isStart) {
          if (booking.details?.confirmationNumber) details.push(<p key="conf"><strong>Confirmation #:</strong> {booking.details.confirmationNumber}</p>);
          if (booking.details?.nameOnReservation) details.push(<p key="name"><strong>Name on Reservation:</strong> {booking.details.nameOnReservation}</p>);
          if (booking.details?.notes) details.push(<p key="notes" style={{ whiteSpace: 'pre-wrap' }}><strong>Notes:</strong> {booking.details.notes}</p>);
        }
        break;
      default:
        return "";
    }
    
    return details.length > 0 ? details : null;
  };

  const renderMenu = () => (
    <div className="details-actions" ref={menuRef}>
      <button className="actions-menu-button" onClick={toggleMenu}>⋮</button>
      {menuOpen && (
        <div className="actions-menu">
          <div className="actions-menu-item" onClick={() => { downloadPDF(); setMenuOpen(false); }}>Download PDF (beta)</div>
          <div className="actions-menu-item" onClick={() => { handleLogout(); setMenuOpen(false); }}>Logout</div>
        </div>
      )}
    </div>
  );

  if (loading) {
    return (
      <div className="App trip-details">
        {renderMenu()}
        <div className="ci-loading">Loading your trip...</div>
      </div>
    );
  }

  return (
    <div className="App trip-details">
      <div className="info-box trip-details-box">
        {renderMenu()}
        {trip ? (
          (() => {
            const startDate = new Date(trip.startDate);
            const endDate = new Date(trip.endDate);
            const durationInDays = Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24)) + 1;

            const days = [];
            const bookingsByDay = new Map();
            
            trip.bookings.forEach(booking => {
              const bookingStart = new Date(booking.startDate);
              const bookingEnd = new Date(booking.endDate);
              const startDateStr = `${bookingStart.getUTCFullYear()}-${String(bookingStart.getUTCMonth() + 1).padStart(2, '0')}-${String(bookingStart.getUTCDate()).padStart(2, '0')}`;
              const endDateStr = `${bookingEnd.getUTCFullYear()}-${String(bookingEnd.getUTCMonth() + 1).padStart(2, '0')}-${String(bookingEnd.getUTCDate()).padStart(2, '0')}`;
            
              if (!bookingsByDay.has(startDateStr)) bookingsByDay.set(startDateStr, []);
              bookingsByDay.get(startDateStr).push({ booking, isStart: true });
            
              if (startDateStr !== endDateStr) {
                if (!bookingsByDay.has(endDateStr)) bookingsByDay.set(endDateStr, []);
                bookingsByDay.get(endDateStr).push({ booking, isStart: false });
              }
            });
            
            let rangeStart = null;
            for (let i = 0; i < durationInDays; i++) {
              const dayDate = new Date(Date.UTC(startDate.getUTCFullYear(), startDate.getUTCMonth(), startDate.getUTCDate() + i));
              const dayDateStr = `${dayDate.getUTCFullYear()}-${String(dayDate.getUTCMonth() + 1).padStart(2, '0')}-${String(dayDate.getUTCDate()).padStart(2, '0')}`;
            
              if (bookingsByDay.has(dayDateStr)) {
                if (rangeStart !== null) {
                  days.push({ start: rangeStart, end: i - 1, bookings: [] });
                  rangeStart = null;
                }
                const dayBookings = bookingsByDay.get(dayDateStr).sort((a, b) => {
                  const timeA = a.isStart ? new Date(a.booking.startDate).getTime() : new Date(a.booking.endDate).getTime();
                  const timeB = b.isStart ? new Date(b.booking.startDate).getTime() : new Date(b.booking.endDate).getTime();
                  return timeA - timeB;
                });
                days.push({ start: i, end: i, bookings: dayBookings });
              } else {
                if (rangeStart === null) rangeStart = i;
                if (i === durationInDays - 1 && rangeStart !== null) {
                  days.push({ start: rangeStart, end: i, bookings: [] });
                }
              }
            }
            
            return (
              <>
                <h2>{trip.destination}</h2>
                <div className="itinerary-container">
                  <div className="itinerary-header">
                    <div className="header-day">Day</div>
                    <div className="header-activities">Activities</div>
                  </div>
                  {days.map((dayRange, index) => {
                    const startDay = new Date(Date.UTC(startDate.getUTCFullYear(), startDate.getUTCMonth(), startDate.getUTCDate() + dayRange.start));
                    const endDay = new Date(Date.UTC(startDate.getUTCFullYear(), startDate.getUTCMonth(), startDate.getUTCDate() + dayRange.end));
            
                    return (
                      <div key={index} className={`day-row ${startDay.toISOString().split('T')[0] === new Date().toUTCString().split(', ')[1].split(' ').slice(1, 4).reverse().join('-') ? 'ci-today-row' : ''}`}>
                        <div className="day-header">
                          <strong>Day {dayRange.start + 1}</strong><br />
                          {new Intl.DateTimeFormat('en-US', { weekday: 'long', timeZone: 'UTC' }).format(startDay)}<br />
                          {new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' }).format(startDay)}, {startDay.getUTCFullYear()}
                          {dayRange.start !== dayRange.end && (
                            <>
                              <br />-<br />
                              <strong>Day {dayRange.end + 1}</strong><br />
                              {new Intl.DateTimeFormat('en-US', { weekday: 'long', timeZone: 'UTC' }).format(endDay)}<br />
                              {new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' }).format(endDay)}, {endDay.getUTCFullYear()}
                            </>
                          )}
                        </div>
                        <div className="bookings-section">
                          {dayRange.bookings.length > 0 ? (
                            <ul className="booking-list">
                              {dayRange.bookings.map(({ booking, isStart }, idx) => {
                                const key = `${booking.id}-${isStart ? 'start' : 'end'}`;
                                const mainInfo = renderBookingMainInfo(booking, isStart);
                                if (!mainInfo) return null;
                                const detailsContent = renderBookingDetails(booking, isStart);
                                return (
                                  <React.Fragment key={key}>
                                    <li className="booking-entry">
                                      <span className={`${getIconClass(booking.type, booking.details, isStart)} icon-entry`}></span>
                                      <div className="booking-details">
                                        <div>{mainInfo}</div>
                                        {detailsContent && (
                                          <button 
                                            className="details-toggle" 
                                            onClick={() => toggleDetails(booking.id, isStart)}
                                          >
                                            {expandedBookings[key] ? 'Hide Details' : 'More Details'}
                                          </button>
                                        )}
                                        {expandedBookings[key] && detailsContent && (
                                          <div className="booking-extra-details">
                                            {detailsContent}
                                          </div>
                                        )}
                                      </div>
                                    </li>
                                    {idx < dayRange.bookings.length - 1 && <hr />}
                                  </React.Fragment>
                                );
                              })}
                            </ul>
                          ) : (
                            <div className="booking-entry no-events">
                              <span className="icon-default icon-entry"></span>
                              <div className="booking-details">No Events Scheduled</div>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
            );
          })()
        ) : (
          <div className="ci-no-trip">No trips to show</div>
        )}
      </div>
    </div>
  );
}

export default ClientItinerary;

